import type { ErrorEnvelope } from '@app/shared';
import type { AxiosError } from 'axios';
import axios, { type AxiosInstance } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

// URL del Swagger UI / OpenAPI, derivada de la base de la API (mismo origen que el
// backend, NO el del frontend). Reemplaza el segmento /api/v1 por /api/docs para que
// funcione en dev (:3000) y en prod (detrás del proxy) sin que la SPA lo intercepte.
export const apiDocsUrl = baseURL.replace(/\/api\/v1\/?$/, '/api/docs');

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true, // imprescindible para cookies httpOnly
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptor: 401 con refresh automático. Si el refresh también falla, redirige a /login.
let refreshing: Promise<void> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ErrorEnvelope>) => {
    const original = error.config;
    if (!original) throw error;

    const isAuthEndpoint =
      original.url?.includes('/auth/login') ||
      original.url?.includes('/auth/refresh') ||
      original.url?.includes('/auth/logout');

    if (error.response?.status === 401 && !isAuthEndpoint && !original.headers?.['x-retry-auth']) {
      try {
        refreshing ??= api.post('/auth/refresh').then(() => undefined);
        await refreshing;
        refreshing = null;

        // Reintentar la request original una sola vez
        original.headers = original.headers ?? {};
        original.headers['x-retry-auth'] = '1';
        return api.request(original);
      } catch {
        refreshing = null;
        // Redirigir a login solo si no estamos ya ahí
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?next=${next}`;
        }
      }
    }
    throw error;
  },
);

/** Resuelve una ruta relativa del API (ej. `/mensajes/:id/audio`) contra el baseURL del cliente. */
export function resolveApiUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  const base = (api.defaults.baseURL ?? '').replace(/\/$/, '');
  return `${base}${path}`;
}

/** Extrae el código de error del envelope del backend. */
export function getErrorCode(error: unknown): string | null {
  if (axios.isAxiosError<ErrorEnvelope>(error)) {
    return error.response?.data?.error?.code ?? null;
  }
  return null;
}

/** Extrae el mensaje legible del envelope del backend. */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<ErrorEnvelope>(error)) {
    return error.response?.data?.error?.message ?? fallback;
  }
  return fallback;
}
